import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCancelRecurringInvoice } from "@/features/invoices/hooks/useRecurringInvoices";

interface CancelRecurringInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recurringInvoiceId: string;
  customerName: string;
}

const CancelRecurringInvoiceDialog = ({
  open,
  onOpenChange,
  recurringInvoiceId,
  customerName,
}: CancelRecurringInvoiceDialogProps) => {
  const cancelMutation = useCancelRecurringInvoice();

  const handleCancel = async (e: React.MouseEvent) => {
    // Prevent AlertDialogAction from auto-closing the dialog
    e.preventDefault();
    try {
      await cancelMutation.mutateAsync(recurringInvoiceId);
      onOpenChange(false);
    } catch {
      // Error handling is done in the hook
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="p-6">
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel Recurring Invoice</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                Are you sure you want to cancel this recurring invoice for{" "}
                <strong className="text-foreground">{customerName}</strong>?
              </p>
              <p>
                This will stop all future invoices from being generated. This
                action cannot be undone.
              </p>
              <p className="text-sm">
                Already-issued invoices will not be affected.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={cancelMutation.isPending}>
            Keep Invoice
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleCancel}
            disabled={cancelMutation.isPending}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {cancelMutation.isPending ? "Cancelling..." : "Cancel Invoice"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CancelRecurringInvoiceDialog;
